import React, { useState } from "react";
import { StarIcon } from "@heroicons/react/solid";
import { trackEvent } from "../analytics";
import { getUtmParams } from "../utils/utm";

export default function ReviewModal({ onClose }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState("");
  const [botField, setBotField] = useState("");
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  function encode(data) {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
      )
      .join("&");
  }

  function validateForm() {
    let newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required.";
    if (!rating) newErrors.rating = "Please select a star rating.";
    if (!review.trim()) newErrors.review = "Review cannot be empty.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (botField) {
      setShowSuccess(true);
      return;
    }
    if (!validateForm()) return;

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "review",
        name,
        company,
        rating: String(rating),
        review,
        "bot-field": botField,
        ...getUtmParams(),
      }),
    })
      .then(() => {
        trackEvent("generate_lead", { form: "review" });
        setShowSuccess(true);
      })
      .catch(() => alert("Error sending your review ❌"));
  }

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4">
        <div className="bg-white text-gray-900 p-6 rounded-xl shadow-lg w-96 text-center border border-gray-200">
          <h3 className="text-xl font-semibold">
            Thanks for your review!{" "}
            <span role="img" aria-label="success">
              ✅
            </span>
          </h3>
          <p className="text-gray-600 mt-2">
            We've received it and will publish it shortly after a quick
            review.
          </p>
          <button
            onClick={onClose}
            className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-full text-white"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 px-4">
      <div className="bg-white text-gray-900 rounded-xl shadow-lg w-full max-w-md border border-gray-200 p-6 text-left max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold mb-1">Leave a Review</h3>
        <p className="text-gray-600 text-sm mb-4">
          Tell other visitors about your experience working with us.
        </p>

        <form name="review" onSubmit={handleSubmit}>
          <p className="hidden" aria-hidden="true">
            <label>
              Leave this field blank
              <input
                name="bot-field"
                tabIndex="-1"
                autoComplete="off"
                value={botField}
                onChange={(e) => setBotField(e.target.value)}
              />
            </label>
          </p>

          <div className="relative mb-4">
            <label
              htmlFor="review-name"
              className="leading-7 text-sm text-gray-600"
            >
              Name
            </label>
            <input
              type="text"
              id="review-name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4"
              required
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div className="relative mb-4">
            <label
              htmlFor="review-company"
              className="leading-7 text-sm text-gray-600"
            >
              Company (optional)
            </label>
            <input
              type="text"
              id="review-company"
              name="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4"
            />
          </div>

          <div className="relative mb-4">
            <span className="leading-7 text-sm text-gray-600">Rating</span>
            <div className="flex gap-1" role="radiogroup" aria-label="Rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  type="button"
                  key={star}
                  aria-label={`${star} star${star > 1 ? "s" : ""}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1"
                >
                  <StarIcon
                    className={`w-7 h-7 ${
                      (hoverRating || rating) >= star
                        ? "text-purple-600"
                        : "text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
            {errors.rating && (
              <p className="text-red-500 text-xs mt-1">{errors.rating}</p>
            )}
          </div>

          <div className="relative mb-4">
            <label
              htmlFor="review-text"
              className="leading-7 text-sm text-gray-600"
            >
              Your Review
            </label>
            <textarea
              id="review-text"
              name="review"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              className="w-full bg-white rounded-lg border border-gray-300 focus:border-purple-500 text-gray-900 py-2 px-4 h-28 resize-none"
              required
            />
            {errors.review && (
              <p className="text-red-500 text-xs mt-1">{errors.review}</p>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 border border-gray-300 py-2 px-6 rounded-full hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="text-white bg-purple-600 border-0 py-2 px-6 hover:bg-purple-700 rounded-full"
            >
              Submit Review
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
